import { FC, ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@design-system";

export interface ITabItem {
  id: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
  count?: number;
}

interface GroupedTabsProps {
  tabs: ITabItem[];
  initialTabId?: string;
  /** Controlled selection, e.g. when a page guards unsaved edits. */
  selectedTabId?: string;
  onTabChange?: (tabId: string) => void;
  ariaLabel?: string;
  variant?: "grouped" | "detail";
}

export const GroupedTabs: FC<GroupedTabsProps> = ({
  tabs,
  initialTabId,
  selectedTabId,
  onTabChange,
  ariaLabel = "Tab selection",
  variant = "grouped",
}) => {
  const { t } = useTranslation();
  const tabsId = useId();
  const [internalTabId, setActiveTabId] = useState<string | undefined>(
    initialTabId || (tabs.length > 0 ? tabs[0].id : undefined),
  );
  const activeTabId = selectedTabId ?? internalTabId;

  useEffect(() => {
    setActiveTabId((currentTabId) => {
      if (currentTabId && tabs.some((tab) => tab.id === currentTabId)) {
        return currentTabId;
      }
      if (initialTabId && tabs.some((tab) => tab.id === initialTabId)) {
        return initialTabId;
      }
      return tabs[0]?.id;
    });
  }, [tabs, initialTabId]);

  const handleTabClick = (tabId: string) => {
    const selectedTab = tabs.find((tab) => tab.id === tabId);
    if (selectedTab && !selectedTab.disabled) {
      if (selectedTabId === undefined) setActiveTabId(tabId);
      if (onTabChange) {
        onTabChange(tabId);
      }
    }
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const tabId = event.target.value;
    const selectedTab = tabs.find((tab) => tab.id === tabId);
    if (selectedTab && !selectedTab.disabled) {
      if (selectedTabId === undefined) setActiveTabId(tabId);
      if (onTabChange) {
        onTabChange(tabId);
      }
    }
  };

  const activeTab = tabs.find((tab) => tab.id === activeTabId);

  if (!tabs || tabs.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Mobile Select */}
      <div className={variant === "detail" ? "hidden" : "sm:hidden"}>
        <label htmlFor={`${tabsId}-select`} className="sr-only">
          {ariaLabel}
        </label>
        <select
          id={`${tabsId}-select`}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus-visible:ring-blue-500 focus-visible:border-blue-500 focus:outline-none block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus-visible:ring-blue-500 dark:focus-visible:border-blue-500 dark:focus:outline-none"
          value={activeTabId}
          onChange={handleSelectChange}
        >
          {tabs.map((tab) => (
            <option key={tab.id} value={tab.id} disabled={tab.disabled}>
              {tab.label}
              {typeof tab.count === "number" && ` (${tab.count})`}
            </option>
          ))}
        </select>
      </div>

      {/* Desktop Tabs */}
      <div
        className={cn(
          "overflow-x-auto border-b border-border",
          variant === "grouped" && "hidden sm:block",
        )}
      >
        <div
          role="tablist"
          aria-label={ariaLabel}
          className={cn(
            "flex min-w-max text-sm font-medium text-muted",
            variant === "grouped" &&
              "w-full text-center rounded-lg shadow-sm dark:divide-border",
          )}
        >
          {tabs.map((tab) => (
            <button
              id={`${tabsId}-${tab.id}-tab`}
              key={tab.id}
              type="button"
              role="tab"
              onClick={() => handleTabClick(tab.id)}
              disabled={tab.disabled}
              className={cn(
                "border-b-2 transition-colors focus-visible:ring-4 focus-visible:ring-brand/30 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                variant === "detail"
                  ? "shrink-0 px-4 py-3 text-left"
                  : "w-full p-4 text-center",
                activeTabId === tab.id
                  ? variant === "detail"
                    ? "border-brand text-text font-medium"
                    : "border-brand text-brand bg-brand-muted"
                  : "border-transparent hover:text-secondary hover:bg-surface-hover",
              )}
              aria-selected={activeTabId === tab.id}
              aria-controls={`${tabsId}-${tab.id}-panel`}
              tabIndex={activeTabId === tab.id ? 0 : -1}
            >
              {tab.label}
              {typeof tab.count === "number" && (
                <span
                  className={cn(
                    "text-xs",
                    variant === "detail"
                      ? "ml-1 font-normal text-muted"
                      : "ml-2 rounded-full px-2 py-0.5 font-medium",
                    variant === "grouped" &&
                      (tab.count > 0
                        ? tab.id === "route"
                          ? "bg-success-muted text-success"
                          : "bg-danger-muted text-danger"
                        : "bg-surface-muted text-secondary"),
                  )}
                >
                  {variant === "detail" ? `(${tab.count})` : tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div
        id={activeTab ? `${tabsId}-${activeTab.id}-panel` : undefined}
        role="tabpanel"
        aria-labelledby={
          activeTab ? `${tabsId}-${activeTab.id}-tab` : undefined
        }
        className="flex-1"
      >
        {activeTab ? (
          activeTab.content
        ) : (
          <div className="p-4">
            {t("common:no_content_available", "No content available.")}
          </div>
        )}
      </div>
    </div>
  );
};
